/**
 * Micro-interazioni Prestige Auto:
 * - inclinazione 3D con riflesso sulle card prodotto/collezione (solo puntatori precisi);
 * - alone rosso che segue il puntatore nell'hero.
 * Tutto disattivato con prefers-reduced-motion.
 */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Aggiorna le variabili CSS al massimo una volta per frame.
  const rafSetter = (el) => {
    let frame = 0;
    let pending = null;
    return (vars) => {
      pending = vars;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        for (const [k, v] of Object.entries(pending)) el.style.setProperty(k, v);
      });
    };
  };

  const MAX_TILT = 5; // gradi

  function bindTilt(card) {
    if (card.dataset.paTilt) return;
    card.dataset.paTilt = '1';
    card.classList.add('pa-tilt');
    const set = rafSetter(card);
    card.addEventListener('pointermove', (e) => {
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      set({
        '--pa-rx': `${((0.5 - y) * MAX_TILT * 2).toFixed(2)}deg`,
        '--pa-ry': `${((x - 0.5) * MAX_TILT * 2).toFixed(2)}deg`,
        '--pa-gx': `${(x * 100).toFixed(1)}%`,
        '--pa-gy': `${(y * 100).toFixed(1)}%`,
      });
    });
    card.addEventListener('pointerleave', () => set({ '--pa-rx': '0deg', '--pa-ry': '0deg' }));
  }

  function bindHeroGlow(hero) {
    if (hero.dataset.paGlow) return;
    hero.dataset.paGlow = '1';
    const set = rafSetter(hero);
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      set({ '--pa-mx': `${e.clientX - r.left}px`, '--pa-my': `${e.clientY - r.top}px` });
    });
  }

  // ---------- Scene legate allo scroll ----------
  // --pa-p (0→1): ingresso di un pannello .pa-rise; --pa-l (0→1): uscita dell'hero.
  const risers = new Set();
  const leavers = new Set();
  let scrollFrame = 0;
  const clamp01 = (v) => Math.min(1, Math.max(0, v));

  // Transizioni tra sezioni: scelte in base al contrasto tra una sezione e la precedente.
  const txSections = new Set();
  const isClear = (c) => !c || c === 'transparent' || /rgba\(.*,\s*0\)$/.test(c);
  function backgroundOf(section) {
    // Prima il contenuto: il wrapper può avere già il fondo di transizione (--pa-under).
    for (const el of [...section.querySelectorAll(':scope > *, :scope > * > *'), section]) {
      const c = getComputedStyle(el).backgroundColor;
      if (!isClear(c)) return c;
    }
    return getComputedStyle(document.body).backgroundColor;
  }
  const luminance = (c) => {
    const [r, g, b] = c.match(/[\d.]+/g).map(Number);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  };
  function setupSectionTransitions() {
    const sections = [
      ...document.querySelectorAll('#MainContent > .shopify-section, .shopify-section-group-footer-group'),
    ].filter((s) => s.offsetHeight > 0);
    const backgrounds = sections.map(backgroundOf); // tutte lette prima di modificare qualcosa
    // Una sezione già visibile all'apertura della pagina non si anima: resta intera
    // (es. footer di una pagina corta, griglia di una collezione).
    const fold = window.innerHeight * 0.9;
    sections.forEach((section, i) => {
      if (i === 0 || section.dataset.paTx || section.querySelector('.pa-rise')) return;
      if (section.getBoundingClientRect().top < fold) {
        section.dataset.paTx = 'none';
        return;
      }
      const under = backgrounds[i - 1];
      const darkBefore = luminance(under) < 0.35;
      const darkNow = luminance(backgrounds[i]) < 0.35;
      // Il footer si apre sempre «verso la notte». Tra sezioni dello stesso tono (tutto il sito
      // è scuro) le transizioni si alternano, così ogni passaggio resta diverso dal precedente.
      const isFooter = section.classList.contains('shopify-section-group-footer-group');
      const cycle = ['rise', 'sweep', 'scan'];
      const type = isFooter
        ? 'dusk'
        : darkBefore !== darkNow
          ? darkNow ? 'dusk' : 'sweep'
          : cycle[txSections.size % cycle.length];
      section.dataset.paTx = type;
      section.classList.add('pa-tx', `pa-tx--${type}`);
      section.style.setProperty('--pa-under', under);
      txSections.add(section);
    });
  }

  /**
   * Avanzamento 0→1 dell'ingresso di una sezione: parte quando il suo bordo alto entra dal basso
   * e finisce quando arriva al 25% dello schermo — oppure, se la pagina finisce prima
   * (es. footer), esattamente quando si arriva in fondo.
   */
  function entryProgress(el, vh) {
    const top = el.getBoundingClientRect().top + window.scrollY;
    const maxScroll = document.documentElement.scrollHeight - vh;
    const start = top - vh;
    const end = Math.min(top - vh * 0.25, maxScroll);
    if (end - start < 1) return 1;
    return clamp01((window.scrollY - start) / (end - start));
  }

  function updateScroll() {
    scrollFrame = 0;
    const vh = window.innerHeight;
    txSections.forEach((el) => {
      if (!el.isConnected) return txSections.delete(el);
      const p = entryProgress(el, vh);
      el.style.setProperty('--pa-p', p.toFixed(3));
      el.classList.toggle('is-tx-done', p >= 0.999);
    });
    for (const set of [risers, leavers]) set.forEach((el) => el.isConnected || set.delete(el));
    risers.forEach((el) => {
      el.style.setProperty('--pa-p', entryProgress(el, vh).toFixed(3));
    });
    leavers.forEach((el) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--pa-l', clamp01(-r.top / (r.height * 0.8)).toFixed(3));
    });
  }
  const requestScroll = () => (scrollFrame ||= requestAnimationFrame(updateScroll));
  window.addEventListener('scroll', requestScroll, { passive: true });
  window.addEventListener('resize', requestScroll, { passive: true });
  // La pagina può crescere dopo il caricamento (es. prodotti consigliati caricati in differita):
  // si ricalcola l'avanzamento, altrimenti il footer resterebbe a metà apertura.
  new ResizeObserver(requestScroll).observe(document.body);

  // ---------- Scroll animato verso le ancore della pagina ----------
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  function scrollToElement(target) {
    const header = document.querySelector('.section-header');
    const offset = header ? header.getBoundingClientRect().height : 0;
    const start = window.scrollY;
    const end = target.getBoundingClientRect().top + start - offset + 1;
    const distance = end - start;
    const duration = Math.min(1400, 600 + Math.abs(distance) * 0.35);
    return new Promise((resolve) => {
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        window.scrollTo({ top: start + distance * easeInOut(p), behavior: 'instant' });
        p < 1 ? requestAnimationFrame(step) : resolve();
      };
      requestAnimationFrame(step);
    });
  }
  function flash(target) {
    if (getComputedStyle(target).position === 'static') target.style.position = 'relative';
    const light = document.createElement('span');
    light.className = 'pa-arrive-flash';
    light.setAttribute('aria-hidden', 'true');
    target.append(light);
    light.addEventListener('animationend', () => light.remove(), { once: true });
  }
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
    const url = new URL(link.href, location.href);
    if (url.pathname !== location.pathname || url.hash.length < 2 || url.hash === '#MainContent') return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    scrollToElement(target).then(() => {
      history.pushState(null, '', url.hash);
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      flash(target);
    });
  });

  function init(root = document) {
    root.querySelectorAll('.pa-rise').forEach((el) => {
      risers.add(el);
      el.classList.add('pa-rise--live');
    });
    root.querySelectorAll('[data-pa-leave]').forEach((el) => leavers.add(el));
    requestScroll();
    if (finePointer) root.querySelectorAll('.product-card-wrapper, .collection-card-wrapper, .pa-finder__card, .pa-channel').forEach(bindTilt);
    root.querySelectorAll('.pa-hero').forEach(bindHeroGlow);
  }

  init();
  setupSectionTransitions();
  requestScroll();
  window.addEventListener('load', () => { setupSectionTransitions(); requestScroll(); }, { once: true });
  // Contenuti ricaricati (filtri collezione, editor del tema, "carica altri").
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    setupSectionTransitions();
  });
  new MutationObserver((records) => {
    for (const r of records) r.addedNodes.forEach((n) => n.nodeType === 1 && init(n));
  }).observe(document.body, { childList: true, subtree: true });
})();
